class SeoExpandable {
  constructor(section) {
    this.section = section;
    this.content = section.querySelector('[data-seo-expandable-content]');
    this.toggle = section.querySelector('[data-seo-expandable-toggle]');

    if (!this.content || !this.toggle) return;

    this.ellipsis = document.createElement('span');
    this.ellipsis.className = 'seo-expandable__ellipsis';
    this.ellipsis.setAttribute('aria-hidden', 'true');
    this.ellipsis.textContent = '...';
    this.content.appendChild(this.ellipsis);

    this.expanded = false;
    this.toggle.addEventListener('click', () => this.setExpanded(!this.expanded));
    window.addEventListener('resize', () => this.update(), { passive: true });
    this.content.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'max-height' && this.expanded) {
        this.content.style.maxHeight = 'none';
      }
    });

    requestAnimationFrame(() => this.update());
    if (document.fonts) {
      document.fonts.ready.then(() => this.update());
    }
  }

  getCollapsedHeight() {
    const heading = this.content.querySelector('h1, h2, h3, h4, h5, h6');
    const headingRect = heading ? heading.getBoundingClientRect() : null;
    const textBlock = Array.from(this.content.querySelectorAll('p, li, blockquote, div')).find((element) => {
      if (!element.textContent.trim() || element.offsetParent === null) return false;
      if (heading && element.contains(heading)) return false;
      if (headingRect && element.getBoundingClientRect().top < headingRect.bottom - 1) return false;
      return true;
    });
    const lineSource = textBlock || this.content;
    const styles = window.getComputedStyle(lineSource);
    const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.6 || 24;
    const contentTop = this.content.getBoundingClientRect().top;
    const textLine = this.getTextLine(heading);

    if (textLine) {
      this.positionEllipsis(textLine);
      return Math.ceil(textLine.bottom - contentTop + 4);
    }

    if (heading) {
      const textRect = textBlock ? textBlock.getBoundingClientRect() : headingRect;
      const textStart = textBlock ? textRect.top - contentTop : headingRect.bottom - contentTop;
      return Math.ceil(textStart + lineHeight * 2 + 4);
    }

    if (textBlock) {
      const childRect = textBlock.getBoundingClientRect();
      return Math.ceil(childRect.top - contentTop + lineHeight * 2 + 4);
    }

    return Math.ceil(lineHeight * 2 + 4);
  }

  getTextLine(heading) {
    const walker = document.createTreeWalker(this.content, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (!node.textContent.trim()) return NodeFilter.FILTER_REJECT;
        if (node.parentElement.closest('.seo-expandable__ellipsis')) return NodeFilter.FILTER_REJECT;
        if (heading && heading.contains(node.parentElement)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    const lineRects = [];

    while (walker.nextNode()) {
      const range = document.createRange();
      range.selectNodeContents(walker.currentNode);
      Array.from(range.getClientRects()).forEach((rect) => {
        if (!rect.width || !rect.height) return;
        lineRects.push(rect);
      });
      range.detach();
    }

    const lines = lineRects
      .sort((a, b) => a.top - b.top || a.left - b.left)
      .reduce((result, rect) => {
        const previous = result[result.length - 1];
        if (previous && Math.abs(previous.top - rect.top) < 3) {
          previous.bottom = Math.max(previous.bottom, rect.bottom);
          previous.right = Math.max(previous.right, rect.right);
          return result;
        }
        result.push({ top: rect.top, right: rect.right, bottom: rect.bottom });
        return result;
      }, []);

    return lines[1] || lines[0] || null;
  }

  positionEllipsis(line) {
    if (!this.ellipsis) return;

    const contentRect = this.content.getBoundingClientRect();
    const ellipsisWidth = this.ellipsis.offsetWidth || 12;
    const left = Math.min(line.right - contentRect.left + 2, this.content.clientWidth - ellipsisWidth);

    this.ellipsis.style.left = `${Math.max(0, left)}px`;
    this.ellipsis.style.top = `${line.top - contentRect.top}px`;
  }

  update() {
    this.collapsedHeight = this.getCollapsedHeight();
    const shouldToggle = this.content.scrollHeight > this.collapsedHeight + 4;
    this.toggle.hidden = !shouldToggle;
    this.section.classList.toggle('seo-expandable--has-toggle', shouldToggle);

    if (!shouldToggle) {
      this.content.style.maxHeight = '';
      this.content.classList.remove('is-collapsed');
      return;
    }

    this.setExpanded(this.expanded);
  }

  setExpanded(expanded) {
    const wasExpanded = this.expanded;
    this.expanded = expanded;
    this.toggle.setAttribute('aria-expanded', String(expanded));
    this.content.classList.toggle('is-collapsed', !expanded);
    this.section.classList.toggle('seo-expandable--expanded', expanded);

    if (expanded) {
      this.content.style.maxHeight = `${this.content.scrollHeight}px`;
      return;
    }

    if (this.content.style.maxHeight === 'none') {
      this.content.style.maxHeight = `${this.content.scrollHeight}px`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.content.style.maxHeight = `${this.collapsedHeight}px`;
          this.scrollToSectionStart(wasExpanded);
        });
      });
      return;
    }

    this.content.style.maxHeight = `${this.collapsedHeight}px`;
    this.scrollToSectionStart(wasExpanded);
  }

  scrollToSectionStart(wasExpanded) {
    if (!wasExpanded) return;

    window.setTimeout(() => {
      const sectionTop = this.section.getBoundingClientRect().top + window.pageYOffset;
      const stickyHeader = document.querySelector('.shopify-section-header-sticky, .section-header, header.header');
      const headerHeight = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;

      window.scrollTo({
        top: Math.max(0, sectionTop - headerHeight - 28),
        behavior: 'smooth',
      });
    }, 80);
  }
}

const initSeoExpandable = () => {
  document.querySelectorAll('[data-seo-expandable-section]').forEach((section) => {
    if (section.dataset.seoExpandableReady) return;
    section.dataset.seoExpandableReady = 'true';
    new SeoExpandable(section);
  });
};

document.addEventListener('DOMContentLoaded', initSeoExpandable);
document.addEventListener('shopify:section:load', initSeoExpandable);
